<template>
  <div v-if="visibleCall" class="floating-call-widget">
    <div class="call-card" :class="{ 'call-card--incoming': isIncoming }">
      <div class="call-content">
        <div class="call-heading">
          <div class="status-badge">
            <span :class="['status-dot', statusDotClass]">
              <fluent-icon icon="call" size="18" />
            </span>
            <div>
              <h3 class="status-title">{{ statusLabel }}</h3>
              <p class="status-subtitle">{{ statusSubtitle }}</p>
            </div>
          </div>
          <span class="duration" :class="{ 'duration--hidden': !showDuration }">
            <fluent-icon icon="clock" size="18" />
            {{ formattedDuration }}
          </span>
        </div>

        <div class="call-details">
          <div class="caller-details">
            <thumbnail :src="callerAvatar" :username="callerName" size="48px" />
            <div class="call-info">
              <p class="caller-name">{{ callerName }}</p>
              <p v-if="callerPhone" class="caller-phone">{{ callerPhone }}</p>
            </div>
          </div>

          <div class="call-card--actions">
            <button
              v-if="isIncoming"
              v-tooltip="
                activeCall
                  ? safeT(
                      'CONVERSATION.VOICE_WIDGET.CANT_ACCEPT_WHILE_ACTIVE',
                      'Encerre a ligação atual para atender'
                    )
                  : null
              "
              class="call-action call-action--accept"
              :disabled="acceptingCallSid === call.callSid || activeCall"
              @click="acceptCall(call)"
            >
              <fluent-icon icon="call" type="filled" size="18" />
              {{
                acceptingCallSid === call.callSid
                  ? 'Atendendo...'
                  : safeT('CONVERSATION.VOICE_WIDGET.ACCEPT', 'Atender')
              }}
            </button>
            <button
              v-if="isIncoming"
              class="call-action call-action--end"
              :disabled="acceptingCallSid === call.callSid"
              @click="rejectCall(call)"
            >
              <fluent-icon icon="call-dismiss" size="18" />
              {{ safeT('CONVERSATION.VOICE_WIDGET.REJECT', 'Recusar') }}
            </button>
            <button
              v-if="isOngoing"
              class="call-action call-action--mute"
              @click="toggleMute"
            >
              <fluent-icon
                :icon="isMuted ? 'microphone-off' : 'microphone'"
                size="18"
              />
              {{
                isMuted
                  ? safeT('CONVERSATION.VOICE_WIDGET.UNMUTE', 'Desmutar')
                  : safeT('CONVERSATION.VOICE_WIDGET.MUTE', 'Mutar')
              }}
            </button>
            <button
              v-if="isOngoing || isOutgoing"
              class="call-action call-action--end"
              @click="endCall"
            >
              <fluent-icon icon="call-dismiss" size="18" />
              {{ safeT('CONVERSATION.VOICE_WIDGET.END', 'Encerrar') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="otherPendingCalls.length" class="pending-calls-list">
      <p class="pending-calls-title">
        {{
          safeT(
            'CONVERSATION.VOICE_WIDGET.PENDING_INCOMING_CALLS',
            'Outras chamadas recebidas'
          )
        }}
      </p>
      <div
        v-for="pendingCall in otherPendingCalls"
        :key="pendingCall.callSid"
        class="pending-call-card"
      >
        <div class="pending-call-info">
          <thumbnail
            :src="callerAvatarFor(pendingCall)"
            :username="callerNameFor(pendingCall)"
            size="36px"
          />
          <div>
            <p class="pending-call-name">{{ callerNameFor(pendingCall) }}</p>
            <p class="pending-call-phone">{{ callerPhoneFor(pendingCall) }}</p>
          </div>
        </div>
        <div class="pending-call-actions">
          <button
            v-tooltip="
              activeCall
                ? safeT(
                    'CONVERSATION.VOICE_WIDGET.CANT_ACCEPT_WHILE_ACTIVE',
                    'Encerre a ligação atual para atender'
                  )
                : null
            "
            class="call-action call-action--accept"
            :disabled="acceptingCallSid === pendingCall.callSid || activeCall"
            @click="acceptCall(pendingCall)"
          >
            <fluent-icon icon="call" type="filled" size="16" />
            {{ safeT('CONVERSATION.VOICE_WIDGET.ACCEPT', 'Atender') }}
          </button>
          <button
            class="call-action call-action--end"
            :disabled="acceptingCallSid === pendingCall.callSid"
            @click="rejectCall(pendingCall)"
          >
            <fluent-icon icon="call-dismiss" size="16" />
            {{ safeT('CONVERSATION.VOICE_WIDGET.REJECT', 'Recusar') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Timer from 'dashboard/helper/Timer';
import whatsappCallSession from 'dashboard/helper/whatsappCallSession';
import ringtoneSrc from 'shared/assets/audio/ringtone.mp3';
import outgoingToneSrc from 'shared/assets/audio/calling_outgoing.mp3';
import Thumbnail from '../Thumbnail.vue';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: { Thumbnail },
  mixins: [alertMixin],
  data() {
    return {
      duration: 0,
      durationTimer: null,
      isMuted: false,
      ringtoneAudio: null,
      ringtoneInterval: null,
      outgoingToneAudio: null,
      acceptingCallSid: null,
    };
  },
  computed: {
    ...mapGetters({
      activeCall: 'calls/activeCall',
      incomingCalls: 'calls/incomingCalls',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
    }),
    visibleCall() {
      if (this.activeCall) return this.activeCall;
      const incoming = this.incomingCalls.find(
        call => !this.isTerminalCall(call)
      );
      return incoming || null;
    },
    pendingIncomingCalls() {
      return this.incomingCalls.filter(call => !this.isTerminalCall(call));
    },
    otherPendingCalls() {
      if (!this.visibleCall) return this.pendingIncomingCalls;
      return this.pendingIncomingCalls.filter(
        call => call.callSid !== this.visibleCall.callSid
      );
    },
    call() {
      return this.visibleCall;
    },
    isOngoing() {
      return this.activeCall?.isActive;
    },
    isIncoming() {
      return !this.call?.isActive && this.call?.callDirection === 'inbound';
    },
    hasPendingIncomingCalls() {
      return this.pendingIncomingCalls.length > 0;
    },
    isOutgoing() {
      return !this.call?.isActive && this.call?.callDirection === 'outbound';
    },
    statusDotClass() {
      if (this.isIncoming) return 'status-dot--incoming';
      if (this.isOngoing) return 'status-dot--ongoing';
      if (this.isOutgoing) return 'status-dot--outgoing';
      return '';
    },
    statusLabel() {
      if (this.isOngoing)
        return this.safeT(
          'CONVERSATION.VOICE_WIDGET.CALL_IN_PROGRESS',
          'Ligação em andamento'
        );
      if (this.isOutgoing)
        return this.safeT(
          'CONVERSATION.VOICE_WIDGET.OUTGOING_CALL',
          'Ligação saindo'
        );
      return this.safeT(
        'CONVERSATION.VOICE_WIDGET.INCOMING_CALL',
        'Ligação recebida'
      );
    },
    statusSubtitle() {
      if (this.isIncoming) return `${this.callerName} está te ligando...`;
      if (
        this.call?.status === 'in-progress' ||
        this.call?.status === 'in_progress'
      ) {
        return this.safeT(
          'CONVERSATION.VOICE_WIDGET.CONNECTED',
          'Chamada conectada'
        );
      }
      return this.safeT(
        'CONVERSATION.VOICE_WIDGET.CONNECTING',
        'Conectando...'
      );
    },
    showDuration() {
      return [
        'in-progress',
        'in_progress',
        'connected',
        'active',
        'accepted',
      ].includes(this.call?.status);
    },
    callerName() {
      return (
        this.call?.caller?.name ||
        this.safeT('CONVERSATION.VOICE_WIDGET.UNKNOWN_CALLER', 'Desconhecido')
      );
    },
    callerPhone() {
      return this.call?.caller?.phone || this.call?.caller?.phoneNumber || '';
    },
    callerAvatar() {
      return this.call?.caller?.avatar || this.call?.caller?.thumbnail || '';
    },
    isOutgoingRinging() {
      return (
        this.activeCall?.callDirection === 'outbound' &&
        this.activeCall?.status === 'ringing'
      );
    },
    isTerminalCall() {
      return call => {
        const terminal = [
          'completed',
          'rejected',
          'no_answer',
          'no-answer',
          'failed',
          'busy',
        ];
        return terminal.includes(call?.status);
      };
    },
    formattedDuration() {
      const mins = Math.floor(this.duration / 60)
        .toString()
        .padStart(2, '0');
      const secs = (this.duration % 60).toString().padStart(2, '0');
      return `${mins}:${secs}`;
    },
  },
  watch: {
    isOutgoingRinging: {
      immediate: true,
      handler(ringing) {
        if (ringing) {
          this.startOutgoingTone();
        } else {
          this.stopOutgoingTone();
        }
      },
    },
    showDuration(showDuration, wasShowingDuration) {
      if (showDuration && !wasShowingDuration) {
        this.startDurationTimer();
      } else if (!showDuration && wasShowingDuration) {
        this.stopDurationTimer();
      }
    },
    pendingIncomingCalls: {
      immediate: true,
      handler(calls) {
        if (calls.length > 0 && !this.activeCall) {
          this.startRingtone();
        } else {
          this.stopRingtone();
        }
      },
    },
  },
  beforeDestroy() {
    this.stopDurationTimer();
    this.stopRingtone();
    this.stopOutgoingTone();
  },
  methods: {
    startOutgoingTone() {
      if (this.outgoingToneAudio) return;

      const audio = new Audio(outgoingToneSrc);
      audio.loop = true;
      audio.play().catch(error => {
        // eslint-disable-next-line no-console
        console.error('[WhatsApp Call] Outgoing tone play failed:', error);
      });
      this.outgoingToneAudio = audio;
    },
    stopOutgoingTone() {
      if (!this.outgoingToneAudio) return;

      this.outgoingToneAudio.pause();
      this.outgoingToneAudio.currentTime = 0;
      this.outgoingToneAudio = null;
    },
    startRingtone() {
      if (this.ringtoneInterval) return;

      const audio = new Audio(ringtoneSrc);
      audio.loop = true;
      audio.volume = 1.0;
      audio.play().catch(error => {
        // eslint-disable-next-line no-console
        console.error('[WhatsApp Call] Ringtone play failed:', error);
      });
      this.ringtoneAudio = audio;

      this.ringtoneInterval = setInterval(() => {
        if (this.ringtoneAudio && this.ringtoneAudio.paused) {
          this.ringtoneAudio.play().catch(() => {});
        }
      }, 1000);
    },
    stopRingtone() {
      if (this.ringtoneInterval) {
        clearInterval(this.ringtoneInterval);
        this.ringtoneInterval = null;
      }
      if (this.ringtoneAudio) {
        this.ringtoneAudio.pause();
        this.ringtoneAudio.currentTime = 0;
      }
    },
    startDurationTimer() {
      this.stopDurationTimer();
      this.duration = 0;
      this.durationTimer = new Timer(elapsed => {
        this.duration = elapsed;
      });
      this.durationTimer.start();
    },
    stopDurationTimer() {
      if (this.durationTimer) {
        this.durationTimer.stop();
        this.durationTimer = null;
      }
    },
    safeT(key, fallback) {
      const translation = this.$t(key);
      return translation && translation !== key ? translation : fallback;
    },
    async acceptCall(call = this.call) {
      if (this.acceptingCallSid) return;
      if (this.activeCall && this.activeCall.callSid !== call?.callSid) {
        this.showAlert(
          this.safeT(
            'CONVERSATION.VOICE_WIDGET.CANT_ACCEPT_WHILE_ACTIVE',
            'Encerre a ligação atual para atender outra.'
          )
        );
        return;
      }
      if (!call || this.isTerminalCall(call)) {
        this.showAlert(
          this.safeT(
            'CONVERSATION.VOICE_WIDGET.CALL_ENDED',
            'A chamada já foi encerrada.'
          )
        );
        return;
      }

      this.acceptingCallSid = call.callSid;
      try {
        await whatsappCallSession.acceptIncomingCall({
          accountId: this.accountId,
          callId: call.callId,
          callSid: call.callSid,
          sdpOffer: call.sdpOffer,
          iceServers: call.iceServers,
          recordingEnabled: call.recordingEnabled,
        });
        this.$store.dispatch('calls/setCallActive', call.callSid);
        this.$store.dispatch('calls/updateCallStatus', {
          callSid: call.callSid,
          status: 'in_progress',
        });
        if (call.conversationId) {
          this.$router.push({
            name: 'inbox_conversation',
            params: { conversation_id: call.conversationId },
          });
        }
      } catch (error) {
        whatsappCallSession.cleanup({ callSid: call.callSid, upload: false });
        this.showAlert(
          error?.response?.data?.error ||
            this.safeT(
              'CONVERSATION.VOICE_WIDGET.ACCEPT_ERROR',
              'Não foi possível atender a ligação.'
            )
        );
      } finally {
        this.acceptingCallSid = null;
      }
    },
    rejectCall(call = this.call) {
      const callSid = call?.callSid;
      const callId = call?.callId;
      if (!callId) return;
      whatsappCallSession.rejectIncomingCall(this.accountId, callId);
      this.$store.dispatch('calls/updateCallStatus', {
        callSid,
        status: 'rejected',
      });
    },
    callerNameFor(call) {
      return (
        call?.caller?.name ||
        this.safeT('CONVERSATION.VOICE_WIDGET.UNKNOWN_CALLER', 'Desconhecido')
      );
    },
    callerPhoneFor(call) {
      return call?.caller?.phone || call?.caller?.phoneNumber || '';
    },
    callerAvatarFor(call) {
      return call?.caller?.avatar || call?.caller?.thumbnail || '';
    },
    endCall() {
      const callId = this.call?.callId;
      if (!callId) return;
      whatsappCallSession.endActiveCall(callId);
      this.$store.dispatch('calls/updateCallStatus', {
        callSid: this.call?.callSid,
        status: 'completed',
      });
    },
    toggleMute() {
      this.isMuted = !this.isMuted;
      const localStream = whatsappCallSession.localStream;
      if (localStream) {
        localStream.getAudioTracks().forEach(track => {
          track.enabled = !this.isMuted;
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/woot';

.floating-call-widget {
  max-width: calc(100% - 2.4rem);
  position: fixed;
  right: 1.2rem;
  top: 1.6rem;
  width: 52rem;
  z-index: 9999;
}

.call-card {
  align-items: flex-start;
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: 1.6rem;
  box-shadow: 0 0.6rem 1.8rem rgba(15, 23, 42, 0.12);
  display: flex;
  padding: 1.6rem 2rem;
}

.call-content {
  flex: 1;
  min-width: 0;
}

.call-heading,
.call-details,
.caller-details,
.duration,
.status-badge,
.call-card--actions,
.call-action {
  align-items: center;
  display: flex;
}

.call-heading,
.call-details {
  justify-content: space-between;
}

.status-badge {
  gap: 1rem;
  min-width: 0;
}

.status-dot {
  align-items: center;
  background: #eaf3ff;
  border-radius: 50%;
  color: #1683ff;
  display: flex;
  flex-shrink: 0;
  height: 3.4rem;
  justify-content: center;
  width: 3.4rem;
}

.status-dot--incoming {
  background: #eafaf1;
  color: #20bf62;
}

.status-dot--ongoing {
  background: #eaf3ff;
  color: #1683ff;
}

.status-dot--outgoing {
  background: #fff4e6;
  color: #f59e0b;
}

.status-title {
  color: #0878f9;
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.call-card--incoming .status-title {
  color: #18b957;
}

.status-subtitle {
  color: #6c83a6;
  font-size: 1.3rem;
  line-height: 1.35;
  margin: 0;
}

.duration {
  color: #607694;
  flex-shrink: 0;
  font-size: 1.4rem;
  font-variant-numeric: tabular-nums;
  gap: 0.4rem;
  margin-left: 1.2rem;
  min-width: 5.2rem;
  visibility: visible;
}

.duration--hidden {
  opacity: 0;
  visibility: hidden;
}

.call-details {
  gap: 1.6rem;
  margin-top: 1.2rem;
}

.caller-details {
  gap: 1rem;
  min-width: 0;
}

.call-info {
  min-width: 0;
}

.caller-name {
  color: #17233a;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.caller-phone {
  color: #7188aa;
  font-size: 1.3rem;
  line-height: 1.35;
  margin: 0.2rem 0 0;
  white-space: nowrap;
}

.call-card--actions {
  flex-shrink: 0;
  gap: 0.8rem;
}

.call-action {
  border: 0;
  border-radius: 1rem;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.3rem;
  font-weight: 700;
  gap: 0.6rem;
  justify-content: center;
  min-height: 4rem;
  min-width: 4rem;
  padding: 0.8rem 1.6rem;
  transition: filter 150ms ease, transform 150ms ease;

  fluent-icon,
  svg {
    display: inline-flex;
    flex-shrink: 0;
  }
}

.call-action:hover {
  filter: brightness(0.96);
  transform: translateY(-1px);
}

.call-action--mute {
  background: #e7edf5;
  color: #455a78;
}

.call-action--accept {
  background: #22c968;
  color: var(--white);
}

.call-action--end {
  background: #ff2f32;
  color: var(--white);
}

.pending-calls-list {
  background: var(--white);
  border: 1px solid var(--s-100);
  border-radius: 1.6rem;
  box-shadow: 0 0.6rem 1.8rem rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 0.8rem;
  max-height: 40vh;
  overflow-y: auto;
  padding: 1.2rem 2rem;
}

.pending-calls-title {
  color: #6c83a6;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
}

.pending-call-card {
  align-items: center;
  display: flex;
  gap: 1.2rem;
  justify-content: space-between;

  & + & {
    border-top: 1px solid var(--s-50);
    padding-top: 0.8rem;
  }
}

.pending-call-info {
  align-items: center;
  display: flex;
  gap: 1rem;
  min-width: 0;
}

.pending-call-name {
  color: #17233a;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pending-call-phone {
  color: #7188aa;
  font-size: 1.2rem;
  margin: 0;
  white-space: nowrap;
}

.pending-call-actions {
  display: flex;
  flex-shrink: 0;
  gap: 0.8rem;

  .call-action {
    min-height: 3.2rem;
    padding: 0.4rem 1.2rem;
  }
}

.call-action:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  transform: none;
}

@media (max-width: 767px) {
  .floating-call-widget {
    left: 1.2rem;
    max-width: none;
    right: 1.2rem;
    top: 1.2rem;
    width: auto;
  }

  .call-card {
    padding: 1.2rem 1.6rem;
  }

  .call-details {
    align-items: stretch;
    flex-direction: column;
    gap: 1.2rem;
  }

  .call-card--actions,
  .call-action {
    flex: 1;
  }

  .call-action {
    min-width: 0;
  }

  .status-title {
    font-size: 1.5rem;
  }

  .status-subtitle {
    font-size: 1.2rem;
  }

  .caller-name {
    font-size: 1.4rem;
  }

  .caller-phone {
    font-size: 1.2rem;
  }

  .call-action {
    min-height: 3.6rem;
    padding: 0.6rem 1.2rem;
  }
}
</style>
